import { PropsWithChildren, createRef, useEffect, CSSProperties } from 'react'
import { styled } from '@mui/styles'
import { TableContainer } from '@mui/material'

export interface IScrollTableProps {
  height?: number | string
  tolerance?: number
  collapse?: boolean
  ssid?: boolean
  isEmpty?: boolean
  shouldFetchNextPage?: boolean
  fetchNextPage?: () => void
  setScrollTop?: (top: number) => void
  setScrollTo?: (fct: () => void) => void
  scrollTo?: boolean
  style?: CSSProperties
}

const ScrollingTable = styled(TableContainer)(({height}: {height: number | string}) => ({
  overflowY: 'scroll',
  height: typeof height === 'number' ? `calc(100cqh - ${height}px)` : height
}))

const ScrollTable = ({
  height = 400,
  tolerance = 0.95,
  shouldFetchNextPage = false,
  fetchNextPage = () => {},
  collapse= false,
  ssid= false,
  children,
  setScrollTop,
  setScrollTo,
  scrollTo,
  style,
}: PropsWithChildren<IScrollTableProps>) => {
  const scrollRef = createRef<HTMLDivElement>()
  const onScroll = (element) => {
    const { clientHeight, scrollTop, scrollHeight } = element.target

    const scrollPosition = clientHeight + scrollTop

    if (scrollPosition > scrollHeight * tolerance) {
      fetchNextPage?.()
    }

    if (setScrollTop && scrollTop > 1) {
      setScrollTop(scrollTop)
    }
  }

  const underScroll = (element) => {
    const { scrollTop } = element.target

    if (setScrollTop && scrollTop > 1) {
      setScrollTop(scrollTop)
    }
  }

  const onScrollTo = (top) => {
    scrollRef?.current?.scrollTo({ top })
  }

  // If screen is too big to display all the data, We need to fetchNextPage
  // The 100 is totally arbitrary, it is needed to avoid fetching when only the header is displayed
  useEffect(() => {
    if (!scrollTo && setScrollTo) {
      setScrollTo(() => onScrollTo)
    }
    if (shouldFetchNextPage && scrollRef?.current && scrollRef.current?.offsetHeight > 100) {
      if (scrollRef?.current?.clientHeight === scrollRef?.current?.scrollHeight) {
        fetchNextPage?.()
      }
    }
  })

  return (
    <ScrollingTable
      sx={{
        borderRadius : collapse ? '8px 0 0 8px' : ssid ? '0' : '8px',
        border: '0px',
        borderTop : '0px',
        ...style
      }}
      height={height}
      onScroll={shouldFetchNextPage ? onScroll : underScroll}
      ref={scrollRef}
    >
      {children}
    </ScrollingTable>
  )
}

export default ScrollTable